/**
 * ESCHER FALLS — 効果音。**すべて合成**（音声ファイルは一切持たない）。
 *
 * 元は無音のスタブだった（CORE-SPEC §6）。実装するにあたっての方針は
 * 「彩度を持つのは水だけ」という配色の原則をそのまま音に写したもので、
 * **石は色を持たない＝音程を持たない**、水だけが少し音程を持つ。
 *
 * | 名前 | 何の音 | 作り |
 * |---|---|---|
 * | `rotate` | 視点を回す。石像が動く（軽） | `stoneMove` の軽い版 |
 * | `lever` | **石橋が動く**（重） | `stoneMove` の重い版＋噛み合う一撃 |
 * | `pour` | 放水の開始。水門が開く | 帯域が上へ開く＋泡を数粒 |
 * | `flow` | **滝**（持続音） | 3層の雑音＋不規則な泡。start/stop で扱う |
 * | `splash` | 行き止まりでこぼれる | 破裂＋低い塊＋泡 |
 * | `wheel` | 水車が回る | 軋みを2回 |
 * | `clear` | 通水 | 鉢を打った音。倍音2本を長く減衰 |
 * | `fail` | 失敗 | 音程を持たない鈍い一撃 |
 *
 * ## 「自然な音」にするために要ったこと
 * 初版は雑音を1発濾して包絡線を掛けるだけだった。石は「シュッ」、水は砂嵐に
 * 聞こえた。質感は**層の重ね方**で決まるので、部品を `texture.ts` に切り出した。
 * 石＝粒の摩擦＋擦れの粒立ち＋胴鳴り＋地鳴り、滝＝3層の雑音＋泡。
 * どの層が何を担っているかは各所のコメントに書いてある。
 *
 * 音程を持つのは `clear` と水系だけにしてある。石に旋律を付けると
 * 「ゲームらしい」音になり、この作品の静けさが壊れる。
 *
 * ## 音量は耳で決めずに測って揃えた
 * 最初に書いた値は音ごとに **25倍** ばらついていた（雑音系は瞬時ピークが低く、
 * 正弦波系は高いため）。ピークではなく**短時間 RMS の最大値**で測り直して揃えた。
 * 平均 RMS で測ると、短い音が無音で薄まって過小評価される。
 *
 * 揃える先は一律ではなく**鳴る頻度で変えてある**。
 * `rotate` は一手ごとに鳴るので控えめに、`clear` は報酬なので前に出す。
 * 測り直すときは docs/VERIFY-NOTES.md の手順を使うこと。
 */

import { audioContext, envelope, noise, seBus } from './engine';
import { between, bubble, grainCloud, modulate, noiseSource, resonantBody, rnd, thud } from './texture';

export type SfxName =
  | 'rotate'
  | 'lever'
  | 'pour'
  | 'splash'
  | 'wheel'
  | 'clear'
  | 'fail'
  | 'connect'
  | 'flow';

/** ミュート。設定の音量 0 とは別に、一括で黙らせたいとき用。 */
let muted = false;

export function setMuted(value: boolean): void {
  muted = value;
}

export function isMuted(): boolean {
  return muted;
}

/** 濾した雑音を1発鳴らす。石の音も水の音もこれが基本になる。 */
function burst(
  ctx: AudioContext,
  out: GainNode,
  opts: {
    at: number;
    peak: number;
    attack: number;
    decay: number;
    type: BiquadFilterType;
    freq: number;
    /** 終わりの周波数。指定すると freq からここへ滑る（擦れて止まる感じが出る） */
    freqTo?: number;
    q?: number;
  },
): void {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  src.loop = true;
  const filter = ctx.createBiquadFilter();
  filter.type = opts.type;
  filter.frequency.setValueAtTime(opts.freq, opts.at);
  if (opts.freqTo !== undefined) {
    filter.frequency.exponentialRampToValueAtTime(
      Math.max(20, opts.freqTo),
      opts.at + opts.attack + opts.decay,
    );
  }
  filter.Q.value = opts.q ?? 1;
  const gain = ctx.createGain();
  envelope(gain, opts.at, opts.peak, opts.attack, opts.decay);
  src.connect(filter).connect(gain).connect(out);
  // ループ再生の位置をずらして、同じ音の連打が機械的に聞こえないようにする。
  src.start(opts.at, (opts.at * 7.13) % 1.5);
  src.stop(opts.at + opts.attack + opts.decay + 0.05);
}

/** 音程のある一発。`clear` と水滴に使う。 */
function tone(
  ctx: AudioContext,
  out: GainNode,
  opts: {
    at: number;
    freq: number;
    peak: number;
    attack: number;
    decay: number;
    type?: OscillatorType;
  },
): void {
  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  const gain = ctx.createGain();
  envelope(gain, opts.at, opts.peak, opts.attack, opts.decay);
  osc.connect(gain).connect(out);
  osc.start(opts.at);
  osc.stop(opts.at + opts.attack + opts.decay + 0.05);
}

/**
 * 石の塊が動く音。**回転とレバーはこれの軽重2種**である。
 *
 * 4層でできている。1層でも欠けると石に聞こえない。
 *   1. 粒の雲   … 摩擦。無数の微小な衝突の連なり（連続した雑音では布になる）
 *   2. 擦れの粒立ち … 2kHz 付近の細かい粒。**これが無いと籠って「ゴボッ」になる。**
 *                    低音だけで重さを出そうとして最初これを落とし、高域が
 *                    低音より 40dB 低い、毛布を被ったような音になった。
 *                    **実機で実際に聞こえているのは主にこの層である**
 *                    （スマホのスピーカーは 300〜500Hz 以下を鳴らせない）
 *   3. 胴鳴り   … 質量。80〜210Hz の共振を立ち上がり遅めに（速いと軽い物になる）
 *   4. 地鳴り   … 体で感じる低さ。50Hz 付近を薄く
 * `impact` を立てると、止まって噛み合う一撃が末尾に付く。
 *
 * ## 実機で「音量が小さい」と言われたときに直すべきもの
 * 音量つまみではなく**帯域**である。この音は energy の7〜8割が 250Hz 以下に
 * 集まっており、スマートフォンのスピーカーはそこをほとんど再生できない。
 * 机の上（フルレンジ出力）で測って揃えても、実機では重さの層が丸ごと消える。
 *
 * 対策は2つで、どちらも「鳴る帯域へ重さを移す」ことに尽きる。
 *   - `resonantBody` に**倍音**を足す。耳が存在しない基音を補って聞くので、
 *     小さなスピーカーでも低く重く感じられる（missing fundamental）
 *   - 擦れの粒立ち（1〜3kHz）を厚くする
 * 単純に音量を上げるだけだと、イヤホンで聴いている人には低音だけが膨らむ。
 */
function stoneMove(
  ctx: AudioContext,
  out: GainNode,
  t: number,
  opts: { dur: number; level: number; impact: boolean },
): void {
  const { dur, level } = opts;

  // 1. 摩擦
  grainCloud(ctx, out, {
    at: t,
    dur: dur * 0.9,
    // 重い方は粒を粗くする。粗い粒はゆっくり擦れている大きな面に聞こえる。
    count: opts.impact ? 26 : 34,
    level: 0.5 * level,
    cutoff: opts.impact ? 1300 : 1200,
    grain: opts.impact ? 0.02 : 0.013,
  });

  // 2. 擦れの粒立ち。**実質この層が主役である。**
  //    スマホのスピーカーが鳴らせるのはここだけなので、他を厚くしても届かない。
  //    Q を広めにとって帯域を稼ぐ（狭いと「シャリ」という色が付く）。
  grainCloud(ctx, out, {
    at: t,
    dur: dur * 0.9,
    count: 46,
    level: 1.4 * level,
    cutoff: opts.impact ? 2000 : 2600,
    type: 'bandpass',
    q: 0.9,
    grain: 0.006,
  });

  // 3. 胴鳴り。石板の固有振動。
  resonantBody(ctx, out, {
    at: t,
    freqs: opts.impact ? [82, 127, 196] : [104, 158, 233],
    level: 0.42 * level,
    // 立ち上がりを遅くとる。**ここが速いと重さが消える。**
    attack: dur * 0.35,
    decay: dur * 0.75,
    q: 8,
  });

  // 4. 地鳴り。**薄くしてある。** スマホでは鳴らず、イヤホンでは濁るだけなので、
  //    重さは倍音（3.）に任せて、ここは土台を薄く敷くに留める。
  {
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(54, t);
    sub.frequency.exponentialRampToValueAtTime(44, t + dur);
    const g = ctx.createGain();
    envelope(g, t, 0.12 * level, dur * 0.4, dur * 0.7);
    sub.connect(g).connect(out);
    sub.start(t);
    sub.stop(t + dur * 1.2 + 0.05);
  }

  // 5. 噛み合って止まる。
  if (opts.impact) thud(ctx, out, { at: t + dur * 0.92, level: 0.34 * level, freq: 96 });
}

export function playSfx(name: SfxName): void {
  if (muted) return;
  const ctx = audioContext();
  const out = seBus();
  // 解錠前（利用者がまだ触っていない）は素通り。呼び出し側は鳴らない前提でよい。
  if (ctx === null || out === null) return;
  const t = ctx.currentTime;

  switch (name) {
    case 'rotate':
      // 石像が動く。軽い版（毎手鳴るので疲れさせない）。
      stoneMove(ctx, out, t, { dur: 0.34, level: 0.22, impact: false });
      break;

    case 'lever':
      // **石橋が動く。** 重い版。長く擦れて、最後に噛み合って止まる。
      stoneMove(ctx, out, t, { dur: 0.62, level: 0.22, impact: true });
      break;

    case 'pour':
      // 水門が開く。滝の音がせり上がってくる導入にする。
      // 雑音の帯域を上へ開くだけだと「シャッ」で終わるので、泡を数粒混ぜる。
      {
        const src = noiseSource(ctx);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.setValueAtTime(300, t);
        lp.frequency.exponentialRampToValueAtTime(4200, t + 0.34);
        lp.Q.value = 0.6;
        const g = ctx.createGain();
        envelope(g, t, 0.24, 0.06, 0.34);
        src.connect(lp).connect(g).connect(out);
        src.start(t, between(0, 1.5));
        src.stop(t + 0.5);
        for (let i = 0; i < 5; i += 1) {
          bubble(ctx, out, { at: t + 0.05 + i * between(0.03, 0.07), level: 0.05 });
        }
      }
      break;

    case 'splash':
      // 水がぶつかる。破裂音だけだと「シャッ」なので、
      // 低い塊（受け止められた水）と泡を重ねる。
      {
        const src = noiseSource(ctx);
        const hp = ctx.createBiquadFilter();
        hp.type = 'bandpass';
        hp.frequency.setValueAtTime(1800, t);
        hp.frequency.exponentialRampToValueAtTime(600, t + 0.3);
        hp.Q.value = 0.5;
        const g = ctx.createGain();
        envelope(g, t, 0.14, 0.008, 0.3);
        src.connect(hp).connect(g).connect(out);
        src.start(t, between(0, 1.5));
        src.stop(t + 0.42);
      }
      thud(ctx, out, { at: t + 0.01, level: 0.06, freq: 150, decay: 0.18 });
      for (let i = 0; i < 8; i += 1) {
        bubble(ctx, out, { at: t + between(0.01, 0.22), level: between(0.03, 0.07) });
      }
      break;

    case 'wheel':
      // 水車の軋み。2回にして「回った」感じを出す。
      burst(ctx, out, { at: t, peak: 0.379, attack: 0.02, decay: 0.18,
        type: 'bandpass', freq: 700, freqTo: 500, q: 4 });
      burst(ctx, out, { at: t + 0.22, peak: 0.3032, attack: 0.02, decay: 0.18,
        type: 'bandpass', freq: 620, freqTo: 450, q: 4 });
      break;

    case 'clear':
      // 鉢を打った音。倍音を2本だけ重ねて長く減衰させる。
      // 派手なファンファーレにしないのは、全面クリア画面と同じ理由
      // （報酬は達成の可視化であって、音量ではない）。
      tone(ctx, out, { at: t, freq: 528, peak: 0.05, attack: 0.006, decay: 1.5 });
      tone(ctx, out, { at: t + 0.01, freq: 792, peak: 0.0225, attack: 0.006, decay: 1.1 });
      tone(ctx, out, { at: t + 0.18, freq: 1056, peak: 0.0125, attack: 0.01, decay: 0.9 });
      break;

    case 'fail':
      // 鈍い一撃。**音程を持たせない。** 失敗を叱る音にしたくない。
      burst(ctx, out, { at: t, peak: 0.448, attack: 0.006, decay: 0.34,
        type: 'lowpass', freq: 260, freqTo: 90 });
      break;

    case 'connect':
      // 接続の成立。**鳴らさない。**
      // ここで音を出すと「つながった」ことを教えてしまい、放水ボタンを
      // 解答オラクルにしなかったのが無意味になる（CORE-SPEC §3.1）。
      break;

    case 'flow':
      // 持続音なので startFlow/stopFlow で扱う。一発物としては何もしない。
      break;
  }
}

// =============================================================================
// 滝の音（持続）
// =============================================================================

/**
 * **滝の音。** 放水中ずっと鳴る。
 *
 * 初版は帯域雑音を正弦波の LFO で揺らしただけで、砂嵐に聞こえた。
 * 滝に聞こえるために必要なのは4つで、どれが欠けても水にならない。
 *
 *   1. **3層に分ける** — 落ちた水の塊（低）／流れの本体（中）／飛沫（高）。
 *      1本の帯域だと筒を通る風の音になる
 *   2. **層ごとに独立に揺らす** — 揺らぎが揃うと1つの物体に聞こえる
 *   3. **揺らぎに周期を持たせない** — 正弦波 LFO は「ワウワウ」と人工的に鳴る。
 *      なめらかな乱数の波を使う（texture.ts の `slowRandom`）
 *   4. **泡を撒く** — これが決め手。短く音程が上がる粒が不規則に混ざって
 *      初めて「水」として聞こえる
 */
let flowNodes: { bus: GainNode; sources: AudioBufferSourceNode[] } | null = null;
let bubbleTimer: number | null = null;

export function startFlow(): void {
  if (muted || flowNodes !== null) return;
  const ctx = audioContext();
  const out = seBus();
  if (ctx === null || out === null) return;
  const t = ctx.currentTime;

  const bus = ctx.createGain();
  bus.gain.setValueAtTime(0.0001, t);
  // 立ち上がりを少しとる。瞬時に最大だと「ザッ」と切り込んで水に聞こえない。
  bus.gain.exponentialRampToValueAtTime(0.16, t + 0.22);
  bus.connect(out);

  const sources: AudioBufferSourceNode[] = [];

  /** 1層ぶん。濾波器の種類と帯域だけを変えて3回使う。 */
  const layer = (
    type: BiquadFilterType,
    freq: number,
    q: number,
    level: number,
    modDepth: number,
    modRate: number,
  ): void => {
    const src = noiseSource(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = level;
    // 音量を揺らす。周波数を揺らすと音程感が出て水から離れる。
    sources.push(modulate(ctx, g.gain, { depth: modDepth, rateHz: modRate, at: t }));
    src.connect(filter).connect(g).connect(bus);
    src.start(t, between(0, 1.5));
    sources.push(src);
  };

  //         種類        中心Hz   Q     音量   揺れ幅  揺れ速さ
  layer('lowpass',      200,   0.7,  0.85,  0.22,  0.6);  // 受け止められた水の塊
  layer('bandpass',     850,   0.35, 0.7,   0.2,   1.1);  // 流れの本体
  layer('highpass',    4200,   0.5,  0.3,   0.14,  1.9);  // 飛沫

  /** 泡を1粒落として、次を予約する。 */
  const scheduleBubble = (): void => {
    if (flowNodes === null) return;
    const now = ctx.currentTime;
    // 一度に1〜3粒。粒が単発で等間隔だと「ピチョン」という時計になる。
    const n = 1 + Math.floor(rnd() * 3);
    for (let i = 0; i < n; i += 1) {
      bubble(ctx, bus, {
        at: now + between(0, 0.09),
        level: between(0.05, 0.13),
        from: between(200, 1100),
        dur: between(0.03, 0.07),
      });
    }
    bubbleTimer = window.setTimeout(scheduleBubble, between(60, 220));
  };
  scheduleBubble();

  flowNodes = { bus, sources };
}

/** 滝を止める。鳴っていなければ何もしない。 */
export function stopFlow(): void {
  if (bubbleTimer !== null) {
    window.clearTimeout(bubbleTimer);
    bubbleTimer = null;
  }
  const ctx = audioContext();
  const nodes = flowNodes;
  flowNodes = null;
  if (nodes === null || ctx === null) return;
  const t = ctx.currentTime;
  // 水が引くように落とす。ぶつ切りにすると耳につく。
  nodes.bus.gain.cancelScheduledValues(t);
  nodes.bus.gain.setValueAtTime(Math.max(0.0001, nodes.bus.gain.value), t);
  nodes.bus.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
  for (const src of nodes.sources) src.stop(t + 0.36);
}
